/**
 * MetricSelectorContainer - Logic for metric dropdown selector
 *
 * Owns the dropdown open-state and `useDropdownPositioning` wiring for the
 * shell-hosted selector (SE-5568 — migrated off the gluestack Menu, whose
 * Android positioning depended on the RN Modal + MenuScrollRoot workaround).
 * Select handlers close the panel and select in the same press; the SE-3823
 * setTimeout deferral is gone with the RN Modal it worked around.
 * @remarks This is a Container component - handles business logic
 */

import { ChevronDown } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import MetricSelectorView from "./MetricSelectorView";

import {
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  useDropdownPositioning,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { METRIC_DROPDOWN_POSITIONING } from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

/**
 * Metric option for the selector
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Props for MetricSelectorContainer component
 */
interface MetricSelectorContainerProps {
  /** Currently selected metric */
  readonly selectedMetric: MetricOption;
  /** Available metric options */
  readonly options: readonly MetricOption[];
  /** Handler for selecting a metric */
  readonly onSelect: (metric: MetricOption) => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
}

/**
 * Get container classes based on theme
 * @param isDark - Whether dark mode is active
 * @returns Tailwind classes for container
 */
const getContainerClasses = (isDark: boolean): string =>
  `flex-row items-center gap-1 rounded-md bg-surface-raised px-3 py-1 ${
    isDark ? "" : "border border-outline-subtle"
  }`;

/**
 * MetricSelectorContainer provides the shell wiring, trigger, and handlers
 * for the metric selector
 * @param props - Container props
 * @param props.selectedMetric - Currently selected metric
 * @param props.options - Available metric options
 * @param props.onSelect - Handler for selecting a metric
 * @param props.isDark - Whether dark mode is active
 * @returns MetricSelectorView with computed props
 */
const MetricSelectorContainer = ({
  selectedMetric,
  options,
  onSelect,
  isDark,
}: MetricSelectorContainerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);

  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen,
    ...METRIC_DROPDOWN_POSITIONING,
  });

  const dropdown = useMemo<ShellDropdownWiring>(
    () => ({ isOpen, onToggle, triggerRef, panelGeometry, shouldShow }),
    [isOpen, onToggle, triggerRef, panelGeometry, shouldShow]
  );

  // Shell-hosted trigger (replaces the gluestack Menu trigger render prop)
  const trigger = useMemo(
    () => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onToggle}
        accessibilityLabel={`Select metric, currently ${selectedMetric.label}`}
      >
        <Stack direction="horizontal" className={getContainerClasses(isDark)}>
          <Text variant="body-strong" className="text-content-primary">
            {selectedMetric.label}
          </Text>
          <Text variant="body-bold" className="text-content-primary">
            {selectedMetric.value != null ? selectedMetric.value : "--"}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-subtle" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [
      isDark,
      isOpen,
      onToggle,
      selectedMetric.label,
      selectedMetric.value,
      triggerRef,
    ]
  );

  // Factory for stable select handlers — closes the panel and selects.
  const createSelectHandler = useCallback(
    (metric: MetricOption) => () => {
      setIsOpen(false);
      onSelect(metric);
    },
    [onSelect]
  );

  return (
    <MetricSelectorView
      selectedMetric={selectedMetric}
      options={options}
      isDark={isDark}
      dropdown={dropdown}
      trigger={trigger}
      createSelectHandler={createSelectHandler}
    />
  );
};

MetricSelectorContainer.displayName = "MetricSelectorContainer";

export default React.memo(MetricSelectorContainer);
